/**
 * Non-color design tokens — radii, elevation, and the 4px spacing scale.
 * Colors live in `colors.ts`.
 */

// Warm-tinted elevation — never neutral black (ADR 0008).
export const shadows = {
	// Hairline lift for admin tiles, the sign-in panel and a field's inset; inner cards are flat tints, not shadows.
	inner: '0 1px 2px rgba(38,18,2,0.06), 0 0 0 1px rgba(38,18,2,0.04)',
	// Resting bento tile / card surface.
	card: '0 4px 12px rgba(84,18,2,0.08), 0 1px 2px rgba(38,18,2,0.04)',
	// Hover / lifted tier — clickable cards only; buttons signal hover with color, not lift.
	cardHover: '0 12px 32px rgba(57,64,128,0.18), 0 2px 6px rgba(38,18,2,0.06)',
	// Dialogs and popovers.
	overlay: '0 12px 32px rgba(84,18,2,0.25)',
	// Keyboard / focus ring around fields and controls.
	focus: '0 0 0 3px rgba(209,95,42,0.12)',
	// Hairline inset ring marking the selected segment or option.
	ring: 'inset 0 0 0 1px rgba(209,95,42,0.2)',
} as const

// Corner radii scale (ADR 0008). Five steps and two full-radius shapes.
export const radii = {
	tiny: '4px', // below the scale: checkboxes, swatches, focus rings
	small: '8px', // chips, tags, menu items
	inner: '12px', // controls: buttons, inputs, nested cards
	card: '16px', // bento tiles / large cards
	band: '24px', // CTA band / sheet headers
	pill: '999px', // a label on a full-radius ground: the Pill primitive, the two pill-shaped buttons
	round: '999px', // geometry, not a label: bars and tracks rounded to their height, dots, circles
} as const

/**
 * Motion durations. `fast` for presses and color changes, `base` for lifts and
 * reveals — the only two speeds a component needs.
 */
export const motion = {
	fast: '120ms ease',
	base: '200ms ease-out',
} as const

/**
 * Responsive breakpoints (px). `bentoCollapse` is shared between the CSS media
 * queries and the JS section reorder, so always interpolate the token rather
 * than writing the number.
 */
export const breakpoints = {
	/** At/below this, phone-density tuning applies (type ramp, rails, compact chrome). */
	phone: 600,
	/** At/below this, two-column forms and side-by-side panels stack. */
	tablet: 768,
	/** At/below this, page-level two-column layouts (admin, balances, the hero) stack. */
	tabletWide: 900,
	/** At/below this, the 12-col bento collapses to the single-column stack. */
	bentoCollapse: 980,
	/** At/above this, grids take their third column. */
	wide: 1200,
} as const

/**
 * The media queries, one per breakpoint, so a styles file writes
 * `${media.phone} { … }` and never a number. The `max` ones read "at/below";
 * `aboveTablet`, `aboveTabletWide` and `wide` read "at/above".
 */
export const media = {
	phone: `@media (max-width: ${breakpoints.phone}px)`,
	tablet: `@media (max-width: ${breakpoints.tablet}px)`,
	tabletWide: `@media (max-width: ${breakpoints.tabletWide}px)`,
	bentoCollapse: `@media (max-width: ${breakpoints.bentoCollapse}px)`,
	aboveTablet: `@media (min-width: ${breakpoints.tablet + 1}px)`,
	aboveTabletWide: `@media (min-width: ${breakpoints.tabletWide + 1}px)`,
	wide: `@media (min-width: ${breakpoints.wide}px)`,
} as const

/**
 * The centered content column width shared by the navbar and page
 * containers so their edges align. Consume via the `contentShell` mixin or
 * `max-width: ${contentWidth}` — never re-type the literal.
 */
export const contentWidth = '1400px'

// 4px base spacing scale.
export const space = {
	1: '4px',
	2: '8px',
	3: '12px',
	4: '16px',
	5: '20px',
	6: '24px',
	7: '28px',
	8: '32px',
	10: '40px',
	12: '48px',
	16: '64px',
} as const

/**
 * Stacking order, lowest to highest. `raised` lifts an element over its
 * siblings inside a component; `sticky` is chrome that stays put on scroll;
 * `float` an in-page panel such as a dropdown that is not portaled; `scrim`
 * covers the page behind an in-page overlay; `modal` is the dialog layer,
 * `viewer` the lightbox that opens above a dialog, and `popover` the portaled
 * dropdowns and tooltips that must clear everything.
 */
export const layers = {
	raised: 1,
	sticky: 40,
	float: 50,
	scrim: 900,
	modal: 1000,
	viewer: 1100,
	popover: 10000,
} as const

/**
 * MUI icon sizes, via `sx={{ fontSize: iconSizes.x }}`: xs beside 13px text,
 * sm beside 14–15px, md the inline glyph in a 16px row, lg a control's glyph,
 * xl a tile's, hero an empty state's.
 */
export const iconSizes = {
	xs: 14,
	sm: 16,
	md: 18,
	lg: 20,
	xl: 28,
	hero: 40,
	art: 120, // placeholder art: the empty profile avatar
} as const

export type ShadowKey = keyof typeof shadows
export type RadiusKey = keyof typeof radii
export type SpaceKey = keyof typeof space
export type BreakpointKey = keyof typeof breakpoints
export type MotionKey = keyof typeof motion
export type MediaKey = keyof typeof media
export type LayerKey = keyof typeof layers
export type IconSizeKey = keyof typeof iconSizes
